import Cookies from 'js-cookie';
import { API } from './index';
import { refreshAPI } from './index';
//import { UserData } from '../types/ApiResponseTypes';

interface AuthDto {
  access: {
    token: string;
    expires: string;
    userId: number;
  };
  refresh: {
    token: string;
    expires: string;
  };
}
const TEST_MODE = process.env.NODE_ENV === 'development';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const ACCESS_TOKEN_EXPIRES_KEY = 'accessTokenExpires';
const REFRESH_TOKEN_EXPIRES_KEY = 'refreshTokenExpires';
const USER_ID_KEY = 'userId';

const setTokens = (authDto: AuthDto): void => {
  const { access, refresh } = authDto;
  Cookies.set(ACCESS_TOKEN_KEY, access.token);
  Cookies.set(REFRESH_TOKEN_KEY, refresh.token);
  Cookies.set(ACCESS_TOKEN_EXPIRES_KEY, access.expires);
  Cookies.set(REFRESH_TOKEN_EXPIRES_KEY, refresh.expires);
  localStorage.setItem(USER_ID_KEY, access.userId.toString());
};

export const getTokens = (): {
  accessToken: string;
  refreshToken: string;
} | null => {
  const accessToken = Cookies.get(ACCESS_TOKEN_KEY);
  const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
  if (accessToken && refreshToken) {
    return { accessToken, refreshToken };
  }
  return null;
};

export const clearTokens = (): void => {
  Cookies.remove(ACCESS_TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
  Cookies.remove(ACCESS_TOKEN_EXPIRES_KEY);
  Cookies.remove(REFRESH_TOKEN_EXPIRES_KEY);
  localStorage.removeItem(USER_ID_KEY);
};

export const loginUser = async (
  email: string,
  password: string
): Promise<AuthDto> => {
  try {
    const response = await API.post<AuthDto>('/api/v1/identity/login', {
      email,
      password,
    });
    setTokens(response.data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const logoutUser = async (): Promise<void> => {
  const tokens = getTokens();
  if (tokens) {
    try {
      await API.post('/api/v1/identity/logout', {
        accessToken: tokens.accessToken,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }
  clearTokens();
};

// ONLY THING THAT USES refreshAPI
export const refreshToken = async (): Promise<AuthDto> => {
  const tokens = getTokens();
  if (!tokens) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await refreshAPI.post<AuthDto>(
      '/api/v1/identity/refresh-token',
      {
        refreshToken: tokens.refreshToken,
      }
    );
    setTokens(response.data);
    return response.data;
  } catch (error: any) {
    clearTokens();
    throw error.response?.data || error;
  }
};

API.interceptors.request.use(
  async (config) => {
    const tokens = getTokens();
    if (!tokens) {
      clearTokens();
      return config;
    }
    const accessTokenExpires = Cookies.get(ACCESS_TOKEN_EXPIRES_KEY);
    const refreshTokenExpires = Cookies.get(REFRESH_TOKEN_EXPIRES_KEY);

    if (
      (refreshTokenExpires && new Date() >= new Date(refreshTokenExpires)) ||
      !refreshTokenExpires
    ) {
      clearTokens();
      window.location.href = '/login';
    }

    if (accessTokenExpires && new Date() >= new Date(accessTokenExpires)) {
      try {
        const newTokens = await refreshToken();
        console.log('used newtoken thing in interceptor');
        config.headers['Authorization'] = `Bearer ${newTokens.access.token}`;
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearTokens();
        window.location.href = '/login';
      }
    } else if (tokens.accessToken) {
      config.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const setupAutoRefresh = () => {
  const checkAndRefresh = async () => {
    const refreshTokenExpires = Cookies.get(REFRESH_TOKEN_EXPIRES_KEY);
    if (!refreshTokenExpires) return;

    const expiresDate = new Date(refreshTokenExpires);
    const now = new Date();
    const timeUntilExpiry = expiresDate.getTime() - now.getTime();

    // Check if the refresh token expires in less than 1 hour
    if (timeUntilExpiry < 60 * 60 * 1000) {
      if (isAuthenticated()) {
        try {
          await refreshToken();
          console.log('Tokens refreshed successfully');
        } catch (error) {
          console.error('Auto refresh failed:', error);
          clearTokens();
          window.location.href = '/login';
          // Handle failed refresh (e.g., redirect to login)
        }
      }
    }
  };

  // Check every hour
  setInterval(checkAndRefresh, 60 * 60 * 1000);

  // Also check on page load
  checkAndRefresh();
};

setupAutoRefresh();

export const isAuthenticated = (): boolean => {
  const tokens = getTokens();
  const accessTokenExpires = Cookies.get(ACCESS_TOKEN_EXPIRES_KEY);
  const refreshTokenExpires = Cookies.get(REFRESH_TOKEN_EXPIRES_KEY);

  if (!tokens || !refreshTokenExpires || !accessTokenExpires) {
    // Log out the user if tokens or refresh token expiration is missing
    clearTokens();
    return false;
  }

  return (
    !!tokens &&
    !!refreshTokenExpires &&
    new Date() < new Date(refreshTokenExpires)
  );
};

export const getAccessToken = (): string | null => {
  const tokens = getTokens();
  return tokens ? tokens.accessToken : null;
};

export const getUserId = (): number | null => {
  const userId = localStorage.getItem(USER_ID_KEY);
  return userId ? parseInt(userId, 10) : null;
};

//RESET PASSWORD

export const resetPassword = async (email: string) => {
  try {
    const response = await API.post('/api/v1/identity/reset-password', {
      email,
    });

    return response;
  } catch (error: any) {
    console.error(error);
  }
};

export const resetPasswordToken = async (
  token: string,
  confirmPassword: string,
  password: string
) => {
  try {
    const response = await API.post(
      `/api/v1/identity/reset-password/${token}`,
      {
        confirmPassword,
        password,
      }
    );
  } catch (error: any) {
    console.error(error);
  }
};

/**
 * Processes Google login and verifies if user exists or needs to be created
 */
export const processGoogleLogin = async (
  email: string,
  name: string,
  googleId: string,
  idToken: string
): Promise<any> => {
  try {
    const response = await API.post<AuthDto>('/api/v1/identity/google-auth', {
      email,
      name,
      googleId,
      idToken,
    });

    // Store Google token for later use (logout, etc.)
    localStorage.setItem('googleToken', idToken);

    // Set regular tokens
    setTokens(response.data);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

/**
 * Checks if a user with the given email exists in the system
 */
export const checkUserExists = async (email: string): Promise<boolean> => {
  try {
    const response = await API.get('/api/v1/identity/user-exists', {
      params: { email },
    });
    return response.data.exists;
  } catch (error: any) {
    console.error('Error checking if user exists:', error);
    return false;
  }
};
